import { getApiClient, logApiError } from './apiClient';

type DashboardParamFilters = {
    dateFrom?: string;
    dateTo?: string;
    company?: string;
    limit?: number;
    includeDocuments?: boolean;
    includeRecentActivity?: boolean;
};

const buildDashboardParams = (filters?: DashboardParamFilters): Record<string, string> => {
    const params: Record<string, string> = {};
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    if (filters?.company) params.company = filters.company;
    if (typeof filters?.limit === 'number' && Number.isFinite(filters.limit)) {
        params.limit = String(filters.limit);
    }
    if (filters?.includeDocuments === false) {
        params.includeDocuments = 'false';
    }
    if (filters?.includeRecentActivity === false) {
        params.includeRecentActivity = 'false';
    }
    return params;
};

// Dashboard data interfaces
export interface DashboardOverview {
    generatedAt?: string;
    overview: {
        totalAWBs: number;
        totalWeight: number;
        totalValue: number;
        totalManifests: number;
        totalCustomers: number;
        totalCompanies: number;
        documentsCount: number;
        avgDeliveryTime: number;
    };
    trends: {
        awbTrend: string;
        manifestTrend: string;
        customerTrend: string;
        revenueTrend: string;
    };
    statusDistribution: Array<{
        status: string;
        count: number;
        percentage: string;
    }>;
    revenueTrend: Array<{
        _id: string;
        revenue: number;
        count: number;
    }>;
    recentActivity: {
        awbs: Array<{
            awbNumber: string;
            currentStatus: string;
            customer: { name: string };
            service: { name: string };
            bookingDate: string;
            totalChargeableWeight: number;
        }>;
        manifests: Array<{
            manifestNo: string;
            manifestNumber?: string;
            status: string;
            vendor: { name: string };
            originHub: { name: string };
            destinationHub: { name: string };
            date: string;
            noOfAwb: number;
        }>;
        customers: Array<{
            name: string;
            email: string;
            customerType: string;
            createdAt: string;
        }>;
    };
    performance: {
        deliverySuccess: number;
        onTimeDelivery: number;
        customerSatisfaction: number;
        avgDeliveryDays: number;
    };
    quickStats: {
        pendingPickups: number;
        inTransit: number;
        outForDelivery: number;
        issues: number;
        delivered: number; // Add delivered property
    };
}

export interface PerformanceMetrics {
    hubPerformance: Array<{
        hubName: string;
        totalShipments: number;
        delivered: number;
        deliveryRate: number;
        avgWeight: number;
        totalRevenue: number;
    }>;
    servicePerformance: Array<{
        _id: string;
        count: number;
        avgDeliveryTime: number;
        revenue: number;
    }>;
}

export interface ActivityFeed {
    activities: Array<{
        awbNumber: string;
        customer: string;
        eventType: string;
        eventDescription: string;
        eventLocation: string;
        eventDate: string;
        currentStatus: string;
    }>;
    documents: Array<{
        originalName: string;
        entityType: string;
        entityId: string;
        uploadedBy: { name: string };
        uploadedAt: string;
        category: string;
    }>;
}

class DashboardService {
    private axiosInstance = getApiClient();

    /**
     * Get dashboard overview with all stats and trends
     */
    async getDashboardOverview(filters?: {
        dateFrom?: string;
        dateTo?: string;
        company?: string;
        includeRecentActivity?: boolean;
        signal?: AbortSignal;
    }): Promise<DashboardOverview> {
        try {
            const params = buildDashboardParams(filters);

            const response = await this.axiosInstance.get('/api/dashboard/overview', {
                params,
                signal: filters?.signal
            });
            return {
                ...response.data.data,
                generatedAt: response.data.generatedAt
            };
        } catch (error: unknown) {
            logApiError('Dashboard Service - Get Overview Error', error);
            throw error;
        }
    }

    /**
     * Get performance metrics by hub and service
     */
    async getPerformanceMetrics(filters?: {
        dateFrom?: string;
        dateTo?: string;
        company?: string;
        signal?: AbortSignal;
    }): Promise<PerformanceMetrics> {
        try {
            const params = buildDashboardParams(filters);

            const response = await this.axiosInstance.get('/api/dashboard/performance', {
                params,
                signal: filters?.signal
            });
            return response.data.data;
        } catch (error: unknown) {
            logApiError('Dashboard Service - Get Performance Error', error);
            throw error;
        }
    }

    /**
     * Get real-time activity feed
     */
    async getActivityFeed(options?: {
        limit?: number;
        includeDocuments?: boolean;
        company?: string;
        dateFrom?: string;
        dateTo?: string;
        signal?: AbortSignal;
    }): Promise<ActivityFeed> {
        try {
            const limit = options?.limit ?? 20;
            const params = buildDashboardParams({
                dateFrom: options?.dateFrom,
                dateTo: options?.dateTo,
                company: options?.company,
                limit,
                includeDocuments: options?.includeDocuments
            });
            const response = await this.axiosInstance.get('/api/dashboard/activity', {
                params,
                signal: options?.signal
            });
            return response.data.data;
        } catch (error: unknown) {
            logApiError('Dashboard Service - Get Activity Feed Error', error);
            throw error;
        }
    }

    /**
     * Get AWB statistics
     */
    async getAWBStats(filters?: {
        company?: string;
        startDate?: string;
        endDate?: string;
        status?: string;
    }): Promise<any> {
        try {
            const params = new URLSearchParams();
            if (filters?.company) params.append('company', filters.company);
            if (filters?.startDate) params.append('startDate', filters.startDate);
            if (filters?.endDate) params.append('endDate', filters.endDate);
            if (filters?.status) params.append('status', filters.status);

            const response = await this.axiosInstance.get(`/api/awb/stats?${params.toString()}`);
            return response.data.data;
        } catch (error: unknown) {
            logApiError('Dashboard Service - Get AWB Stats Error', error);
            throw error;
        }
    }

    /**
     * Get manifest statistics
     */
    async getManifestStats(filters?: {
        dateFrom?: string;
        dateTo?: string;
        vendor?: string;
        originHub?: string;
        destinationHub?: string;
    }): Promise<any> {
        try {
            const params = new URLSearchParams();
            if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
            if (filters?.dateTo) params.append('dateTo', filters.dateTo);
            if (filters?.vendor) params.append('vendor', filters.vendor);
            if (filters?.originHub) params.append('originHub', filters.originHub);
            if (filters?.destinationHub) params.append('destinationHub', filters.destinationHub);

            const response = await this.axiosInstance.get(`/api/manifests/stats?${params.toString()}`);
            return response.data.data;
        } catch (error: unknown) {
            logApiError('Dashboard Service - Get Manifest Stats Error', error);
            throw error;
        }
    }

    /**
     * Get tracking statistics
     */
    async getTrackingStats(filters?: {
        dateFrom?: string;
        dateTo?: string;
        company?: string;
    }): Promise<any> {
        try {
            const params = new URLSearchParams();
            if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
            if (filters?.dateTo) params.append('dateTo', filters.dateTo);
            if (filters?.company) params.append('company', filters.company);

            const response = await this.axiosInstance.get(`/api/tracking/stats?${params.toString()}`);
            return response.data.data;
        } catch (error: unknown) {
            logApiError('Dashboard Service - Get Tracking Stats Error', error);
            throw error;
        }
    }
}

// Export singleton instance
export const dashboardService = new DashboardService();
export default dashboardService; 
